import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Menu, X } from "lucide-react";
import { HERO_IMG, ACTION_IMG, HUDDLE_IMG, FEATURES, STATS } from "@/components/landing/landingData";

export function LandingNav({ scrolled, mobileOpen, setMobileOpen }) {
  return (
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled ? "bg-[#0B0D12]/95 border-b border-surface-3 backdrop-blur" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-brand">
            <div className="w-9 h-9 bg-amber flex items-center justify-center">
              <span className="font-display text-2xl text-[#0B0D12] leading-none">G</span>
            </div>
            <div className="hidden sm:block">
              <div className="font-display text-lg tracking-widest text-white leading-none">
                GRIDIRON OS
              </div>
              <div className="font-condensed text-[9px] tracking-[0.4em] uppercase text-amber leading-none mt-1">
                Program Operating System
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <a
              href="#features"
              className="text-xs font-condensed uppercase tracking-[0.25em] text-slate-300 hover:text-amber transition-colors"
              data-testid="nav-features"
            >
              Modules
            </a>
            <a
              href="#how"
              className="text-xs font-condensed uppercase tracking-[0.25em] text-slate-300 hover:text-amber transition-colors"
              data-testid="nav-how"
            >
              How It Works
            </a>
            <Link
              to="/pricing"
              className="text-xs font-condensed uppercase tracking-[0.25em] text-slate-300 hover:text-amber transition-colors"
              data-testid="nav-pricing"
            >
              Pricing
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="hidden sm:inline text-xs font-condensed uppercase tracking-[0.25em] text-slate-300 hover:text-amber transition-colors"
              data-testid="nav-login"
            >
              Sign In
            </Link>
            <Link to="/register" data-testid="nav-cta">
              <Button className="h-10 px-5 bg-amber hover:bg-amber-600 text-[#0B0D12] font-condensed uppercase tracking-[0.25em] text-xs rounded-none">
                Get Started
              </Button>
            </Link>
            <button
              className="md:hidden text-slate-300"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-surface-3 bg-[#0B0D12]">
            <div className="px-6 py-4 flex flex-col gap-4">
              <a href="#features" onClick={() => setMobileOpen(false)} className="text-xs font-condensed uppercase tracking-[0.25em] text-slate-300">Modules</a>
              <a href="#how" onClick={() => setMobileOpen(false)} className="text-xs font-condensed uppercase tracking-[0.25em] text-slate-300">How It Works</a>
              <Link to="/pricing" onClick={() => setMobileOpen(false)} className="text-xs font-condensed uppercase tracking-[0.25em] text-slate-300">Pricing</Link>
              <Link to="/login" onClick={() => setMobileOpen(false)} className="text-xs font-condensed uppercase tracking-[0.25em] text-slate-300">Sign In</Link>
            </div>
          </div>
        )}
      </header>
  );
}

export function HeroSection() {
  return (
      <section className="relative min-h-[92vh] flex items-end overflow-hidden pt-24" data-testid="hero">
        <img
          src={HERO_IMG}
          alt="American football players in formation on the field"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D12] via-[#0B0D12]/85 to-[#0B0D12]/40" />
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, transparent 0, transparent 44px, rgba(255,255,255,0.12) 44px, rgba(255,255,255,0.12) 45px)",
          }}
        />

        <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-10 pb-20 lg:pb-28">
          <div className="max-w-4xl">
            <div className="text-[10px] font-condensed tracking-[0.5em] uppercase text-amber mb-6" data-testid="hero-eyebrow">
              The Program Operating System
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.9] tracking-wide text-white uppercase" data-testid="hero-headline">
              Every play.
              <br />
              Every practice.
              <br />
              <span className="text-amber">Every philosophy.</span>
            </h1>
            <p className="mt-8 text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl font-light" data-testid="hero-sub">
              Gridiron OS is the digital memory of a football staff. Playbook, practice
              plans, film breakdowns, culture, and an AI coordinator — one command center
              built for the way real coaches actually work.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/register" data-testid="hero-cta-primary">
                <Button className="h-14 px-8 bg-amber hover:bg-amber-600 text-[#0B0D12] font-condensed uppercase tracking-[0.3em] text-sm rounded-none w-full sm:w-auto">
                  Start Your Program
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-cta-secondary">
                <Button
                  variant="outline"
                  className="h-14 px-8 bg-transparent border border-white/30 hover:border-amber hover:text-amber text-white font-condensed uppercase tracking-[0.3em] text-sm rounded-none w-full sm:w-auto"
                >
                  Coach Sign In
                </Button>
              </Link>
            </div>

            <div className="mt-12 hairline max-w-md" />
            <div className="mt-6 text-xs font-condensed tracking-[0.4em] uppercase text-slate-500">
              Built for coaches. By the standards you set.
            </div>
          </div>
        </div>
      </section>
  );
}

export function StatsStrip() {
  return (
      <section className="border-y border-surface-3 bg-[#0B0D12]" data-testid="stats-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-3 gap-6">
          {STATS.map((s) => (
            <div key={s.label} className="text-center sm:text-left">
              <div className="font-display text-5xl sm:text-6xl text-amber leading-none">
                {s.n}
              </div>
              <div className="mt-3 text-[10px] sm:text-xs font-condensed tracking-[0.3em] uppercase text-slate-400">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>
  );
}

export function FeaturesSection() {
  return (
      <section id="features" className="py-24 lg:py-32 bg-background" data-testid="features">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="text-[10px] font-condensed tracking-[0.5em] uppercase text-amber mb-4">
              Six Modules. One System.
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-wide text-white uppercase leading-[0.95]">
              The Whole Program.
              <br />
              <span className="text-amber">In One Command Center.</span>
            </h2>
            <div className="hairline mt-8 max-w-md" />
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-surface-3 border border-surface-3">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  data-testid={f.testid}
                  className="group bg-background p-8 lg:p-10 hover:bg-surface transition-colors duration-300 flex flex-col"
                >
                  <div className="w-12 h-12 border border-surface-3 group-hover:border-amber flex items-center justify-center mb-6 transition-colors">
                    <Icon className="w-5 h-5 text-amber" />
                  </div>
                  <div className="font-display text-2xl tracking-wider text-white uppercase mb-3 leading-tight">
                    {f.title}
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed font-light flex-1">
                    {f.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
  );
}

export function HowItWorksSection() {
  return (
      <section id="how" className="relative py-24 lg:py-40 overflow-hidden" data-testid="how">
        <img
          src={ACTION_IMG}
          alt="American football players in action during a tackle"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0B0D12]/85" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-[10px] font-condensed tracking-[0.5em] uppercase text-amber mb-4">
                How It Works
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-wide text-white uppercase leading-[0.95]">
                From Whiteboard
                <br />
                To <span className="text-amber">Signal-Caller</span>
              </h2>
              <p className="mt-8 text-slate-300 leading-relaxed font-light max-w-lg">
                Every note you'd normally lose lives inside Gridiron OS. The AI reads your
                install, your culture doc, and your opponent film — then coaches with you
                in the same voice your program speaks.
              </p>
            </div>

            <div className="space-y-6">
              {[
                { n: "01", t: "Install Your Program", d: "Upload playbook, culture docs, staff manual. Seeded demo included so you can play immediately." },
                { n: "02", t: "Build the Week", d: "Drag drills and plays into a practice plan. Print field cards. Share with staff — instantly." },
                { n: "03", t: "Coach With The AI", d: "Ask questions, generate scout reports, brainstorm scripts. The AI knows your team." },
              ].map((step) => (
                <div key={step.n} className="border-l-2 border-amber pl-6 py-2" data-testid={`step-${step.n}`}>
                  <div className="font-display text-3xl text-amber leading-none">{step.n}</div>
                  <div className="font-display text-xl tracking-wider text-white uppercase mt-2">
                    {step.t}
                  </div>
                  <div className="text-sm text-slate-400 mt-2 leading-relaxed font-light max-w-md">
                    {step.d}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
  );
}

export function FinalCTA() {
  return (
      <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="final-cta">
        <img
          src={HUDDLE_IMG}
          alt="American football team huddle strategizing"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0D12] via-[#0B0D12]/95 to-[#0B0D12]" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-[10px] font-condensed tracking-[0.5em] uppercase text-amber mb-6">
            Your Season Starts Here
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-wide text-white uppercase leading-[0.95]">
            Run Your Program
            <br />
            <span className="text-amber">Like The Pros Do.</span>
          </h2>
          <p className="mt-8 text-slate-300 leading-relaxed font-light max-w-xl mx-auto">
            Head coaches, coordinators, and position coaches — one system, one source of
            truth. Start free, publish your playbook, meet your AI coordinator today.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" data-testid="cta-register">
              <Button className="h-14 px-8 bg-amber hover:bg-amber-600 text-[#0B0D12] font-condensed uppercase tracking-[0.3em] text-sm rounded-none w-full sm:w-auto">
                Start Your Program
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing" data-testid="cta-pricing">
              <Button
                variant="outline"
                className="h-14 px-8 bg-transparent border border-white/30 hover:border-amber hover:text-amber text-white font-condensed uppercase tracking-[0.3em] text-sm rounded-none w-full sm:w-auto"
              >
                See Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
  );
}

export function LandingFooter() {
  return (
      <footer className="border-t border-surface-3 bg-[#0B0D12] py-10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-amber flex items-center justify-center">
              <span className="font-display text-lg text-[#0B0D12] leading-none">G</span>
            </div>
            <div className="font-display text-sm tracking-widest text-white">
              GRIDIRON OS
            </div>
          </div>
          <div className="text-[10px] font-condensed tracking-[0.4em] uppercase text-slate-500">
            © {new Date().getFullYear()} Gridiron OS · Built For Coaches
          </div>
          <div className="flex gap-6">
            <Link to="/login" className="text-[10px] font-condensed uppercase tracking-[0.3em] text-slate-400 hover:text-amber">
              Sign In
            </Link>
            <Link to="/pricing" className="text-[10px] font-condensed uppercase tracking-[0.3em] text-slate-400 hover:text-amber">
              Pricing
            </Link>
          </div>
        </div>
      </footer>
  );
}
